import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { QueryClient } from '@tanstack/angular-query-experimental';

import {
  UserActionStatsWidget,
  injectUserActionStatsQuery,
} from 'src/app/api/queries/client-admin.queries';
import {
  UserActionStatsRangeParams,
  UserActionStatsResponse,
} from 'src/app/api/services/client-admin-api.service';
import { selectCoreDarkMode } from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';
import {
  cssToken,
  dashboardAxisLabels,
  dataViewChartBase,
  dataViewTooltipTheme,
} from 'src/app/shared/utils/highcharts-theme.utils';

type PeriodDays = 7 | 14 | 30;

interface PeriodOption {
  days: PeriodDays;
  labelKey: string;
}

const WIDGET_TITLE_KEY: Record<UserActionStatsWidget, string> = {
  hourly_active_users: 'user-action-stats-widgets.widget.hourly-active-users',
  top_action_types: 'user-action-stats-widgets.widget.top-user-action-types',
  top_active_companies: 'user-action-stats-widgets.widget.top-active-companies',
  errors_per_hour: 'user-action-stats-widgets.widget.errors-per-hour',
};

/** Widgets that take the whole row of the group grid. */
const FULL_WIDTH_WIDGETS: ReadonlySet<UserActionStatsWidget> = new Set([
  'hourly_active_users',
  'errors_per_hour',
]);

/** Widgets whose buckets are hours, not days. */
const HOURLY_WIDGETS: ReadonlySet<UserActionStatsWidget> = new Set([
  'hourly_active_users',
  'errors_per_hour',
]);

const HOUR_GAP = '+1HOUR';

/**
 * User activity widget group of the admin dashboards (WAP-1762): the
 * user-action aggregations over a day-based period, drawn as one chart per
 * widget on the shared data-view recipe and Highcharts theme, sized for the
 * DevOps wall displays: hourly active users and errors per hour as full-width
 * lines, the top 20 companies and the top 20 action types ranked over the
 * period as horizontal bars. The host names the widgets to show
 * and their order. The group owns its period select
 * and Refresh; every chart re-derives when the theme flips.
 */
@Component({
  selector: 'app-user-action-stats-widgets',
  templateUrl: './user-action-stats-widgets.component.html',
  styleUrl: './user-action-stats-widgets.component.scss',
  standalone: false,
})
export class UserActionStatsWidgetsComponent {
  /** The widgets to show, in order. */
  readonly widgets = input.required<UserActionStatsWidget[]>();

  private readonly store = inject(Store<AppState>);
  private readonly translateService = inject(TranslateService);
  private readonly queryClient = inject(QueryClient);

  protected readonly Highcharts: typeof Highcharts = Highcharts;

  protected readonly periodOptions: readonly PeriodOption[] = [
    { days: 7, labelKey: 'user-action-stats-widgets.period.last-7-days' },
    { days: 14, labelKey: 'user-action-stats-widgets.period.last-14-days' },
    { days: 30, labelKey: 'user-action-stats-widgets.period.last-30-days' },
  ];

  protected readonly selectedPeriodDays = signal<PeriodDays>(7);
  private readonly darkMode = toSignal(this.store.select(selectCoreDarkMode));

  /** The active period, shown in brackets next to every widget title. */
  protected readonly periodLabel = computed(() =>
    this.translateService.instant(
      this.periodOptions.find(
        (option) => option.days === this.selectedPeriodDays(),
      )!.labelKey,
    ),
  );

  private readonly range = computed(() =>
    this.buildRangeParams(this.selectedPeriodDays()),
  );

  /** The widget's range while it is shown; the errors run per hour, the rest per day. */
  private rangeFor(widget: UserActionStatsWidget) {
    return computed(() => {
      if (!this.widgets().includes(widget)) {
        return null;
      }
      const range = this.range();
      return HOURLY_WIDGETS.has(widget) ? { ...range, gap: HOUR_GAP } : range;
    });
  }

  private readonly queries = {
    hourly_active_users: injectUserActionStatsQuery(
      'hourly_active_users',
      this.rangeFor('hourly_active_users'),
    ),
    top_action_types: injectUserActionStatsQuery(
      'top_action_types',
      this.rangeFor('top_action_types'),
    ),
    top_active_companies: injectUserActionStatsQuery(
      'top_active_companies',
      this.rangeFor('top_active_companies'),
    ),
    errors_per_hour: injectUserActionStatsQuery(
      'errors_per_hour',
      this.rangeFor('errors_per_hour'),
    ),
  };

  protected readonly loading = computed(() =>
    this.widgets().some((widget) => this.queries[widget].isFetching()),
  );

  /** One entry per shown widget: its query state and the chart options derived from its data. */
  protected readonly views = computed(() =>
    this.widgets().map((widget) => {
      const query = this.queries[widget];
      return {
        key: widget,
        titleKey: WIDGET_TITLE_KEY[widget],
        fullWidth: FULL_WIDTH_WIDGETS.has(widget),
        pending: query.isPending(),
        refreshing: query.isFetching() && !!query.data(),
        failed: query.isError(),
        options: this.optionsFor(widget, query.data()),
      };
    }),
  );

  protected onPeriodChange(days: PeriodDays): void {
    this.selectedPeriodDays.set(days);
  }

  protected refresh(): void {
    this.queryClient.invalidateQueries({
      queryKey: ['client_admin', 'user_action_stats'],
    });
  }

  private optionsFor(
    widget: UserActionStatsWidget,
    response: UserActionStatsResponse | undefined,
  ): Highcharts.Options | null {
    // * Series fills are baked to literals via getComputedStyle (Highcharts
    // * applies them as SVG presentation attributes, where var() cannot work),
    // * so the options must re-derive when the theme flips.
    this.darkMode();
    if (!response) {
      return null;
    }
    switch (widget) {
      case 'hourly_active_users':
        return this.buildHourlyLineChart(
          response,
          '--color-feedback-info-solid',
        );
      case 'top_active_companies':
        return this.buildRankedBarChart(
          response.series.map((s) => ({
            name: s.name,
            total: s.data.reduce((sum, value) => sum + value, 0),
          })),
        );
      case 'top_action_types':
        return this.buildRankedBarChart(
          response.categories.map((name, index) => ({
            name,
            total: response.series[0]?.data[index] ?? 0,
          })),
        );
      default:
        return this.buildHourlyLineChart(
          response,
          '--color-feedback-danger-solid',
        );
    }
  }

  /** Day-aligned range in UTC, the `+1DAY` gap of the aggregation. */
  private buildRangeParams(days: PeriodDays): UserActionStatsRangeParams {
    const oneDayMs = 86_400_000;
    const todayUtcMidnight = Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
    );
    const endTs = todayUtcMidnight + oneDayMs;
    const startTs = endTs - days * oneDayMs;
    return {
      from: this.formatYmd(startTs),
      to: this.formatYmd(endTs),
      gap: '+1DAY',
    };
  }

  private formatYmd(timestampMs: number): string {
    const d = new Date(timestampMs);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /** Shared chrome of the charts: the data-view base at dashboard height, themed axes and tooltip, no built-in title. */
  private chartBase(categories: string[]): Highcharts.Options {
    const base = dataViewChartBase();
    return {
      ...base,
      chart: { ...base.chart, height: 380 },
      plotOptions: {
        ...base.plotOptions,
        column: { ...base.plotOptions?.column, cursor: undefined },
      },
      xAxis: {
        categories,
        labels: dashboardAxisLabels(),
        lineColor: cssToken('--color-border-default'),
        tickLength: 0,
      },
      yAxis: {
        title: { text: undefined },
        labels: dashboardAxisLabels(),
        gridLineColor: cssToken('--color-border-default'),
        allowDecimals: false,
      },
      tooltip: { ...dataViewTooltipTheme(), shared: true },
    };
  }

  /**
   * An hourly series over the period: `+1HOUR` buckets whose categories are
   * `YYYY-MM-DDTHH:MM` in UTC, drawn on a datetime axis in local time so a
   * week reads as 168 points with day ticks.
   */
  private buildHourlyLineChart(
    response: UserActionStatsResponse,
    colorToken: string,
  ): Highcharts.Options {
    const base = this.chartBase([]);
    return {
      ...base,
      time: { useUTC: false },
      xAxis: {
        ...base.xAxis,
        type: 'datetime',
        categories: undefined,
      },
      tooltip: { ...base.tooltip, xDateFormat: '%d %b %H:%M' },
      series: response.series.map(
        (s): Highcharts.SeriesLineOptions => ({
          type: 'line',
          name: s.name,
          data: s.data.map((value, index) => [
            Date.parse(`${response.categories[index]}:00Z`),
            value,
          ]),
          color: cssToken(colorToken),
          marker: { enabled: false },
          lineWidth: 2,
        }),
      ),
    };
  }

  /** Entries ranked by their total over the whole period, one horizontal bar each (companies, action types). */
  private buildRankedBarChart(
    entries: Array<{ name: string; total: number }>,
  ): Highcharts.Options {
    const ranked = [...entries].sort((a, b) => b.total - a.total);
    const base = this.chartBase(ranked.map((entry) => entry.name));
    return {
      ...base,
      chart: {
        ...base.chart,
        height: Math.max(380, ranked.length * 30 + 60),
      },
      tooltip: { ...base.tooltip, shared: false },
      series: [
        {
          type: 'bar',
          name: this.translateService.instant(
            'user-action-stats-widgets.series.actions',
          ),
          data: ranked.map((entry) => entry.total),
          color: cssToken('--color-feedback-info-solid'),
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '14px',
              fontWeight: 'normal',
              color: 'var(--color-text-secondary)',
              textOutline: 'none',
            },
          },
        },
      ],
    };
  }
}
